"use client";

/**
 * Cache local de registros por dominio.
 *
 * Llave única: `${domain}:${id}`. Todos los dominios viven en el mismo
 * objectStore `cache` para simplificar migraciones.
 *
 * Items pueden estar en estados:
 *   - normal: data tal cual del servidor.
 *   - _pending: true → optimista, todavía no confirmado por servidor.
 *   - _pendingDelete: true → eliminado offline, oculto en listados.
 */

import {
  CHANNELS,
  notify,
  reqAsPromise,
  STORES,
  txReadOnly,
  txReadWrite,
} from "./db";

export type CacheEntry<T = Record<string, unknown>> = {
  key: string;          // `${domain}:${id}`
  domain: string;
  id: string | number;
  data: T;
  updatedAt: number;
  _pending?: boolean;
  _pendingDelete?: boolean;
};

function buildKey(domain: string, id: string | number): string {
  return `${domain}:${id}`;
}

// ── Read ────────────────────────────────────────────────────────────────

export async function getCacheEntry<T = Record<string, unknown>>(
  domain: string,
  id: string | number
): Promise<CacheEntry<T> | null> {
  return txReadOnly<CacheEntry<T> | null>(STORES.cache, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const r = (await reqAsPromise(s.get(buildKey(domain, id)))) as
      | CacheEntry<T>
      | undefined;
    return r ?? null;
  });
}

export async function listCache<T = Record<string, unknown>>(
  domain: string,
  options: { includeDeleted?: boolean } = {}
): Promise<CacheEntry<T>[]> {
  return txReadOnly<CacheEntry<T>[]>(STORES.cache, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const idx = s.index("domain");
    const range = IDBKeyRange.only(domain);
    const all = (await reqAsPromise(idx.getAll(range))) as CacheEntry<T>[];
    if (options.includeDeleted) return all;
    return all.filter((e) => !e._pendingDelete);
  });
}

// ── Write ───────────────────────────────────────────────────────────────

export async function putCacheEntry<T = Record<string, unknown>>(
  domain: string,
  id: string | number,
  data: T,
  flags: { pending?: boolean; pendingDelete?: boolean } = {}
): Promise<void> {
  const entry: CacheEntry<T> = {
    key: buildKey(domain, id),
    domain,
    id,
    data,
    updatedAt: Date.now(),
    ...(flags.pending ? { _pending: true } : {}),
    ...(flags.pendingDelete ? { _pendingDelete: true } : {}),
  };
  await txReadWrite(STORES.cache, async (_tx, store) => {
    const s = store as IDBObjectStore;
    await reqAsPromise(s.put(entry));
  });
  notify(CHANNELS.cache);
}

/**
 * Merge shallow: combina el data nuevo con el existente, preservando campos
 * que el item nuevo no incluye (ej. relaciones cargadas previamente desde
 * /api/{domain}/[id] cuando llega un subset desde /api/{domain}).
 */
function mergeData<T extends Record<string, unknown>>(
  existing: T | undefined,
  next: T
): T {
  if (!existing) return next;
  return { ...existing, ...next };
}

/**
 * Reemplaza la cache de un dominio con la lista dada (no toca otros dominios).
 * Conserva entries con `_pending` o `_pendingDelete` y hace merge shallow para
 * preservar campos ricos cargados previamente (productos/fincas/siembras/etc).
 */
export async function replaceDomainCache<T extends { id: string | number }>(
  domain: string,
  items: T[]
): Promise<void> {
  await txReadWrite(STORES.cache, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const idx = s.index("domain");
    const range = IDBKeyRange.only(domain);
    const existing = (await reqAsPromise(idx.getAll(range))) as CacheEntry[];
    const existingMap = new Map(existing.map((e) => [e.key, e]));

    const protectedKeys = new Set(
      existing.filter((e) => e._pending || e._pendingDelete).map((e) => e.key)
    );

    // Borrar entries que ya no aparecen en la lista nueva (excepto _pending).
    const incomingKeys = new Set(items.map((i) => buildKey(domain, i.id)));
    for (const e of existing) {
      if (protectedKeys.has(e.key)) continue;
      if (incomingKeys.has(e.key)) continue;
      await reqAsPromise(s.delete(e.key));
    }

    // Upsert con merge shallow para preservar campos ricos.
    const now = Date.now();
    for (const item of items) {
      const key = buildKey(domain, item.id);
      if (protectedKeys.has(key)) continue;
      const prev = existingMap.get(key);
      const merged = mergeData(
        prev?.data as Record<string, unknown> | undefined,
        item as unknown as Record<string, unknown>
      );
      await reqAsPromise(
        s.put({
          key,
          domain,
          id: item.id,
          data: merged,
          updatedAt: now,
        } as CacheEntry)
      );
    }
  });
  notify(CHANNELS.cache);
}

/**
 * Reemplazo ACOTADO: upsert de `items` + borrado de las entries del dominio que
 * cumplen `match` y NO están en `items` (respetando _pending/_pendingDelete).
 * Para datos que el server REGENERA con ids nuevos (p.ej. la programación
 * semanal: deleteMany+createMany en cada edición de un C/P) — un merge puro
 * acumularía las filas viejas como duplicados fantasma offline. `match` acota
 * el reemplazo a la ventana re-fetcheada (p.ej. las semanas del mes pedido) sin
 * tocar el resto del dominio acumulado.
 */
export async function replaceDomainCacheWhere<T extends { id: string | number }>(
  domain: string,
  items: T[],
  match: (data: Record<string, unknown>) => boolean
): Promise<void> {
  await txReadWrite(STORES.cache, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const idx = s.index("domain");
    const range = IDBKeyRange.only(domain);
    const existing = (await reqAsPromise(idx.getAll(range))) as CacheEntry[];
    const incomingKeys = new Set(items.map((i) => buildKey(domain, i.id)));
    for (const e of existing) {
      if (e._pending || e._pendingDelete) continue;
      if (incomingKeys.has(e.key)) continue;
      let matches = false;
      try {
        matches = match(e.data as Record<string, unknown>);
      } catch {
        matches = false;
      }
      if (matches) await reqAsPromise(s.delete(e.key));
    }
    const now = Date.now();
    const existingMap = new Map(existing.map((e) => [e.key, e]));
    for (const item of items) {
      const key = buildKey(domain, item.id);
      const prev = existingMap.get(key);
      if (prev && (prev._pending || prev._pendingDelete)) continue;
      const merged = mergeData(
        prev?.data as Record<string, unknown> | undefined,
        item as unknown as Record<string, unknown>
      );
      await reqAsPromise(
        s.put({ key, domain, id: item.id, data: merged, updatedAt: now } as CacheEntry)
      );
    }
  });
  notify(CHANNELS.cache);
}

/**
 * Upsert de items SIN borrar los demás del dominio. Para listados paginados
 * donde cada fetch sólo trae una página y queremos acumular en cache.
 * Respeta `_pending` y `_pendingDelete`.
 */
export async function mergeDomainCache<T extends { id: string | number }>(
  domain: string,
  items: T[]
): Promise<void> {
  await txReadWrite(STORES.cache, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const now = Date.now();
    for (const item of items) {
      const key = buildKey(domain, item.id);
      const existing = (await reqAsPromise(s.get(key))) as
        | CacheEntry
        | undefined;
      if (existing && (existing._pending || existing._pendingDelete)) {
        // Mantener cambios locales sin sincronizar.
        continue;
      }
      // Merge shallow: preserva campos ricos del detalle (productos/fincas/
      // siembras) cuando el listado trae un subset.
      const merged = mergeData(
        existing?.data as Record<string, unknown> | undefined,
        item as unknown as Record<string, unknown>
      );
      await reqAsPromise(
        s.put({
          key,
          domain,
          id: item.id,
          data: merged,
          updatedAt: now,
        } as CacheEntry)
      );
    }
  });
  notify(CHANNELS.cache);
}

export async function removeCacheEntry(
  domain: string,
  id: string | number
): Promise<void> {
  await txReadWrite(STORES.cache, async (_tx, store) => {
    const s = store as IDBObjectStore;
    await reqAsPromise(s.delete(buildKey(domain, id)));
  });
  notify(CHANNELS.cache);
}

/**
 * Promueve un id temporal a su id real.
 *
 * IMPORTANTE: hace MERGE shallow `{...existing, ...serverData}` en vez de
 * reemplazar. Si el usuario hizo PATCH offline después del POST (cambios
 * locales aún en el outbox), `serverData` no los incluye — pero queremos
 * mantenerlos visibles en la UI hasta que el PATCH se sincronice. El merge
 * preserva campos que el servidor no tocó.
 *
 * Si serverData incluye un campo nulo explícito (el server lo borró), ese
 * null sobrescribe el local — eso es deseable.
 */
export async function promoteTempId<T = Record<string, unknown>>(
  domain: string,
  tmpId: string,
  realId: string | number,
  serverData?: T
): Promise<void> {
  await txReadWrite(STORES.cache, async (_tx, store) => {
    const s = store as IDBObjectStore;
    const tmpKey = buildKey(domain, tmpId);
    const existing = (await reqAsPromise(s.get(tmpKey))) as
      | CacheEntry<T>
      | undefined;
    await reqAsPromise(s.delete(tmpKey));
    let data: T | undefined;
    if (serverData != null && existing?.data != null && typeof serverData === "object" && typeof existing.data === "object") {
      data = { ...(existing.data as Record<string, unknown>), ...(serverData as Record<string, unknown>) } as T;
    } else {
      data = serverData ?? existing?.data;
    }
    if (data == null) return;
    await reqAsPromise(
      s.put({
        key: buildKey(domain, realId),
        domain,
        id: realId,
        data,
        updatedAt: Date.now(),
      } as CacheEntry<T>)
    );
  });
  notify(CHANNELS.cache);
}
